/**
 * useDashboard Hook
 *
 * Custom hook for dashboard document management.
 * Provides a simplified interface to the dashboard store with common operations.
 *
 * @module features/dashboard/hooks/useDashboard
 * @see SPEC-016 Requirement 1.1
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DocListItemInput } from '@linkode/shared';
import {
  useDashboardStore,
  selectDocs,
  selectTotal,
  selectHasMore,
  selectIsLoading,
  selectError,
  selectViewMode,
  selectSelectedDocIds,
  selectSortBy,
  selectSortOrder,
  selectSearchQuery,
  selectCurrentFolderId,
  selectHasHydrated,
  type ViewMode,
  type SortBy,
  type SortOrder,
} from '../store/dashboardStore';
import { dashboardApiClient } from '../api/dashboardApiClient';

// ============================================================================
// Types
// ============================================================================

export interface UseDashboardReturn {
  // State
  docs: DocListItemInput[];
  total: number;
  hasMore: boolean;
  isLoading: boolean;
  error: ReturnType<typeof selectError>;
  viewMode: ViewMode;
  selectedDocIds: string[];
  sortBy: SortBy;
  sortOrder: SortOrder;
  searchQuery: string;
  currentFolderId: string | null;
  hasHydrated: boolean;
  isCreatingDoc: boolean;

  // Actions
  fetchDocs: () => Promise<void>;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  setViewMode: (mode: ViewMode) => void;
  setSortBy: (sortBy: SortBy) => void;
  setSortOrder: (sortOrder: SortOrder) => void;
  setSearchQuery: (query: string) => void;
  setCurrentFolder: (folderId: string | null) => void;
  toggleDocSelection: (docId: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
  deleteDoc: (docId: string) => Promise<void>;
  moveDoc: (docId: string, targetFolderPath: string) => Promise<void>;
  clearError: () => void;

  // Navigation
  openDoc: (doc: DocListItemInput) => void;
  createNewDoc: () => Promise<void>;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useDashboard Hook
 *
 * Provides a unified interface for dashboard document operations.
 *
 * Features:
 * - Auto-fetch documents on mount (after hydration)
 * - Document navigation
 * - View mode management
 * - Sort and filter operations
 * - Selection management
 * - Document CRUD operations
 *
 * @example
 * ```tsx
 * function DashboardPage() {
 *   const {
 *     docs,
 *     isLoading,
 *     viewMode,
 *     setViewMode,
 *     openDoc,
 *     createNewDoc,
 *   } = useDashboard();
 *
 *   return (
 *     <div>
 *       <ViewModeToggle value={viewMode} onChange={setViewMode} />
 *       {viewMode === 'list' && (
 *         <ListView docs={docs} onDocClick={openDoc} />
 *       )}
 *     </div>
 *   );
 * }
 * ```
 *
 * @see Requirement 1.1
 */
export function useDashboard(): UseDashboardReturn {
  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────────────────
  // State Selectors
  // ─────────────────────────────────────────────────────────────────────────

  const docs = useDashboardStore(selectDocs);
  const total = useDashboardStore(selectTotal);
  const hasMore = useDashboardStore(selectHasMore);
  const isLoading = useDashboardStore(selectIsLoading);
  const error = useDashboardStore(selectError);
  const viewMode = useDashboardStore(selectViewMode);
  const selectedDocIds = useDashboardStore(selectSelectedDocIds);
  const sortBy = useDashboardStore(selectSortBy);
  const sortOrder = useDashboardStore(selectSortOrder);
  const searchQuery = useDashboardStore(selectSearchQuery);
  const currentFolderId = useDashboardStore(selectCurrentFolderId);
  const hasHydrated = useDashboardStore(selectHasHydrated);

  // ─────────────────────────────────────────────────────────────────────────
  // Store Actions
  // ─────────────────────────────────────────────────────────────────────────

  const fetchDocs = useDashboardStore((state) => state.fetchDocs);
  const loadMore = useDashboardStore((state) => state.loadMore);
  const refresh = useDashboardStore((state) => state.refresh);
  const setViewMode = useDashboardStore((state) => state.setViewMode);
  const setSortBy = useDashboardStore((state) => state.setSortBy);
  const setSortOrder = useDashboardStore((state) => state.setSortOrder);
  const setSearchQuery = useDashboardStore((state) => state.setSearchQuery);
  const setCurrentFolder = useDashboardStore((state) => state.setCurrentFolder);
  const toggleDocSelection = useDashboardStore((state) => state.toggleDocSelection);
  const selectAll = useDashboardStore((state) => state.selectAll);
  const clearSelection = useDashboardStore((state) => state.clearSelection);
  const deleteDoc = useDashboardStore((state) => state.deleteDoc);
  const moveDoc = useDashboardStore((state) => state.moveDoc);
  const clearError = useDashboardStore((state) => state.clearError);

  // ─────────────────────────────────────────────────────────────────────────
  // Local State for Document Creation
  // ─────────────────────────────────────────────────────────────────────────

  const [isCreatingDoc, setIsCreatingDoc] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // Navigation Actions
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Navigate to document editor
   */
  const openDoc = useCallback(
    (doc: DocListItemInput) => {
      navigate(`/editor/${doc.docId}`);
    },
    [navigate]
  );

  /**
   * Create a new document via API and navigate to editor
   * @see SPEC-016 Requirement 9.2
   */
  const createNewDoc = useCallback(async () => {
    if (isCreatingDoc) return;

    setIsCreatingDoc(true);
    try {
      const { docId } = await dashboardApiClient.createDoc();
      navigate(`/editor/${docId}`);
    } catch (error) {
      console.error('[useDashboard] Failed to create document:', error);
      // Could set an error state here if needed
    } finally {
      setIsCreatingDoc(false);
    }
  }, [navigate, isCreatingDoc]);

  // ─────────────────────────────────────────────────────────────────────────
  // Auto-fetch on mount
  // ─────────────────────────────────────────────────────────────────────────

  // Track if initial fetch has been triggered to prevent duplicate fetches
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Wait for hydration before fetching
    // Only fetch once on mount, not on every render
    if (hasHydrated && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      // Use the store's fetchDocs directly to avoid dependency issues
      // Wrap in async IIFE to handle the promise properly
      (async () => {
        try {
          await useDashboardStore.getState().fetchDocs();
        } catch {
          // Error is already handled in the store
          // This catch prevents unhandled promise rejection
        }
      })();
    }
  }, [hasHydrated]); // Remove fetchDocs from dependencies to prevent infinite loop

  // ─────────────────────────────────────────────────────────────────────────
  // Return
  // ─────────────────────────────────────────────────────────────────────────

  return {
    // State
    docs,
    total,
    hasMore,
    isLoading,
    error,
    viewMode,
    selectedDocIds,
    sortBy,
    sortOrder,
    searchQuery,
    currentFolderId,
    hasHydrated,
    isCreatingDoc,

    // Actions
    fetchDocs,
    loadMore,
    refresh,
    setViewMode,
    setSortBy,
    setSortOrder,
    setSearchQuery,
    setCurrentFolder,
    toggleDocSelection,
    selectAll,
    clearSelection,
    deleteDoc,
    moveDoc,
    clearError,

    // Navigation
    openDoc,
    createNewDoc,
  };
}
